import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Section, SectionHead } from '../components/ui/Section';
import { Reveal } from '../components/ui/Reveal';
import { Media } from '../components/ui/Media';
import { MetaRow } from '../components/ui/MetaRow';
import { PullQuote } from '../components/ui/PullQuote';
import { Button } from '../components/ui/Button';
import { getProject } from '../data/work';
export function CaseStudy() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const project = slug ? getProject(slug) : undefined;
  useEffect(() => {
    window.scrollTo(0, 0);
    if (project) {
      document.title = `${project.name} — Case Study | Yetu Design Lab`;
    }
  }, [project]);
  if (!project) return <Navigate to="/#work" replace data-id="element-344" data-mp="10u9a432ki5hd" />;
  const next = getProject(project.next);
  return <Layout data-id="element-345" data-mp="15tlvskhpd6id">
      {/* Cover */}
      <Section aria-labelledby="case-title" data-id="element-346" data-mp="15sstbvps1361">
        <Reveal onMount data-id="element-347" data-mp="15rhrw2jt07i3">
          <Link to="/#work" className="link t-label text-mut" data-id="element-348" data-mp="3c4h41ooaoe9b">
            ← All work
          </Link>
          <p className="t-label mt-10 text-accent" data-id="element-349" data-mp="376fnbaeccr10">Case study</p>
          <h1 id="case-title" className="t-h1 mt-6 max-w-[16ch] text-balance" data-id="element-350" data-mp="37436j0kbkmpj">
            {project.name}
          </h1>
          <p className="t-lead measure mt-8 text-mut" data-id="element-351" data-mp="3gvz7qkhpcsxz">{project.intro}</p>
        </Reveal>

        <Reveal className="mt-16" data-id="element-352" data-mp="2t64vo557j8es">
          <Media src={project.cover.src} alt={project.cover.alt} caption={project.cover.caption} ratio={project.cover.ratio} priority data-id="element-353" data-mp="2t7674aq4xwx1" />
        </Reveal>
      </Section>

      {/* Facts */}
      <Section className="!pt-0" data-id="element-354" data-mp="2x7q33ayz2uhl">
        <Reveal data-id="element-355" data-mp="2x7gkw9k65rmg">
          <MetaRow aria-label="Project details" items={[{
          label: 'Client',
          value: project.client
        }, {
          label: 'Industry',
          value: project.industry
        }, {
          label: 'Timeline',
          value: project.timeline
        }, {
          label: 'Services',
          value: project.services
        }]} data-id="element-356" data-mp="2x6ykr1udyt99" />
        </Reveal>
      </Section>

      {/* Challenge */}
      <Section ruled aria-labelledby="challenge-title" data-id="element-357" data-mp="3b70l1ugrwxz6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-358" data-mp="3f9nhk8oe372p">
          <Reveal className="lg:col-span-5" data-id="element-359" data-mp="3k99t8dokkf9e">
            <p className="t-label text-subtle" data-id="element-360" data-mp="3k870s1blm4rd">The challenge</p>
            <h2 id="challenge-title" className="t-h2 mt-5 max-w-[16ch] text-balance" data-id="element-361" data-mp="2nj976mfl4cvs">
              {project.challenge.heading}
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-7" data-id="element-362" data-mp="2wjxk1dt8s773">
            <div className="measure-tight space-y-6" data-id="element-363" data-mp="2wi4gi7dxe33b">
              {project.challenge.paragraphs.map((p, i) => <p key={i} className="t-body text-mut" data-id="element-364" data-mp="31cgz6f7kvae0" data-mpi={i}>
                  {p}
                </p>)}
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Approach */}
      <Section ruled aria-labelledby="approach-title" data-id="element-365" data-mp="28s09dq1j3l7k">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-366" data-mp="219ixjd66jubf">
          <Reveal className="lg:col-span-5" data-id="element-367" data-mp="1wazatajriei0">
            <p className="t-label text-subtle" data-id="element-368" data-mp="1wc0m9g4ox309">The approach</p>
            <h2 id="approach-title" className="t-h2 mt-5 max-w-[16ch] text-balance" data-id="element-369" data-mp="1rbmpiu8jhfhj">
              {project.approach.heading}
            </h2>
            <ul className="mt-10 space-y-4" data-id="element-370" data-mp="1ibg5epic5h9e">
              {project.approach.colors.map(color => <li key={color.hex} className="flex items-center gap-4" data-id="element-371" data-mp="1dkhl7eu87z0v">
                  <span className="h-8 w-8 rounded border border-line-2" style={{
                background: color.hex
              }} aria-hidden="true" data-id="element-372" data-mp="1dj6gncsyin8p" />
                  <span className="t-label text-mut" data-id="element-373" data-mp="zl8fxa7k6pbo">
                    {color.name}
                    <span className="sr-only" data-id="element-374" data-mp="zn1e0143w182"> — {color.hex}</span>
                  </span>
                </li>)}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-7" data-id="element-375" data-mp="2upo97avkefg3">
            <div className="measure-tight space-y-6" data-id="element-376" data-mp="2ypyrnn0jmybu">
              {project.approach.paragraphs.map((p, i) => <p key={i} className="t-body text-mut" data-id="element-377" data-mp="2kytquhlfvzzx" data-mpi={i}>
                  {p}
                </p>)}
            </div>
            <dl className="mt-12 border-b border-line" data-id="element-378" data-mp="2pwdda4sajj2y">
              {project.approach.capabilities.map(cap => <div key={cap.title} className="border-t border-line py-6" data-id="element-379" data-mp="2az2s23puiyu1">
                  <dt className="t-h3" data-id="element-380" data-mp="2g0juw4rw4vd9">{cap.title}</dt>
                  <dd className="t-body measure mt-2 text-mut" data-id="element-381" data-mp="21zpi79xt888s">{cap.text}</dd>
                </div>)}
            </dl>
          </Reveal>
        </div>
      </Section>

      {/* Selected screens */}
      <Section ruled aria-labelledby="screens-title" data-id="element-382" data-mp="8m6ehc6onmn">
        <SectionHead eyebrow="Selected screens" title="One system, everywhere it lives." titleId="screens-title" data-id="element-383" data-mp="94a1z16e44l" />
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2" data-id="element-384" data-mp="fauoaxk0v78d">
          {project.screens.map((screen, i) => <Reveal key={screen.src} delayStep={i} className={project.screens.length % 2 === 1 && i === 0 ? 'md:col-span-2' : undefined} data-id="element-385" data-mp="adcb2uyz25uk" data-mpi={i}>
              <Media src={screen.src} alt={screen.alt} caption={screen.caption} ratio={project.screens.length % 2 === 1 && i === 0 ? '16/9' : screen.ratio} contain={screen.contain} plate={screen.plate} data-id="element-386" data-mp="y1vjrblxokn6" />
            </Reveal>)}
        </div>
      </Section>

      {/* Quote */}
      <Section ruled data-id="element-387" data-mp="38u6rwgqimw0r">
        <Reveal data-id="element-388" data-mp="38vq7e54cl31h">
          <PullQuote author={project.quote.author} role={project.quote.role} data-id="element-389" data-mp="34slbilervzlt">
            “{project.quote.text}”
          </PullQuote>
        </Reveal>
      </Section>

      {/* Results */}
      <Section ruled aria-labelledby="results-title" data-id="element-390" data-mp="1cqs856uw1whv">
        <Reveal data-id="element-391" data-mp="1craewxf6ft41">
          <h2 id="results-title" className="t-h2 mb-12" data-id="element-392" data-mp="1csuzbu7s1vo6">
            The results
          </h2>
          <MetaRow scale="figure" items={project.results.map(r => ({
          label: r.label,
          value: r.value,
          note: r.note
        }))} data-id="element-393" data-mp="1hry5jhzhu7xq" />
        </Reveal>
      </Section>

      {/* Next project */}
      {next && <Section ruled aria-labelledby="next-title" data-id="element-394" data-mp="1vntil777oot7">
          <Reveal data-id="element-395" data-mp="20ftemok5uxki">
            <p className="t-label text-subtle" data-id="element-396" data-mp="20he8ebynichb">Next project</p>
            <Link to={`/work/${next.slug}`} className="group mt-6 block" data-id="element-397" data-mp="25f6atzwx9gzr">
              <h2 id="next-title" className="t-h1 max-w-[16ch] transition-colors group-hover:text-accent" data-id="element-398" data-mp="25himxwg00x4w">
                {next.name}
              </h2>
            </Link>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-399" data-mp="2sskviyfbwhg">
              <Button to="/#contact" variant="solid" data-id="element-400" data-mp="2qo96eua9t6m">
                Book a free consultation
              </Button>
              <Button to="/#work" variant="text" data-id="element-401" data-mp="3pxgppgbdwkvn">
                Back to all work
              </Button>
            </div>
          </Reveal>
        </Section>}
    </Layout>;
}